import React, { useMemo, useRef, useState } from 'react';
import PdaPage from '../components/PdaPage';
import {
  bindGeneratedDeliveryNotePallets,
  DeliveryPalletStockItem,
  DeliveryNotePalletItemPayload,
  GeneratedDeliveryNote,
  GeneratedDeliveryNoteLine,
  listDeliveryPalletStocks,
  listGeneratedDeliveryNotes,
} from '../services/api';
import { usePersistentFocus } from '../hooks/usePersistentFocus';
import {
  buildDeliveryPalletBindPayload,
  extractDeliveryNotePalletItems,
  DeliveryNotePalletDisplayItem,
  findGeneratedDeliveryNoteByScanCode,
  getGeneratedDeliveryNoteBindId,
  getGeneratedDeliveryNoteNo,
} from '../utils/deliveryPalletBind';
import { normalizeScannedCode } from '../utils/pdaCodes';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifySuccess } from '../utils/pdaFeedback';
import { navigateTo } from '../utils/navigation';
import { getStoredUser } from '../utils/session';

type PalletBindSuccessItem = {
  materialNo: string;
  quantity: number;
};

type PalletStockRow = {
  key: string;
  materialNo: string;
  description: string;
  stockQty: number;
  availableQty: number;
  frozenQty: number;
  status: string;
};

function normalizeText(value: unknown) {
  return String(value ?? '').trim();
}

function toFiniteNumber(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function pickValue(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
}

function extractList<T>(data: unknown): T[] {
  if (Array.isArray(data)) {
    return data as T[];
  }
  if (!data || typeof data !== 'object') {
    return [];
  }
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.items)) {
    return record.items as T[];
  }
  if (Array.isArray(record.list)) {
    return record.list as T[];
  }
  if (Array.isArray(record.data)) {
    return record.data as T[];
  }
  return [];
}

function mapPalletStock(item: DeliveryPalletStockItem, index: number): PalletStockRow {
  const record = item as Record<string, unknown>;
  const materialNo = normalizeText(pickValue(record, ['material_no', 'materialNo']));
  const stockQty = toFiniteNumber(pickValue(record, ['stock_qty', 'stockQty']));
  const frozenQty = toFiniteNumber(pickValue(record, ['frozen_qty', 'frozenQty', 'reserved_qty', 'reservedQty']));
  const availableQty = toFiniteNumber(
    pickValue(record, ['available_qty', 'availableQty']),
    Math.max(0, stockQty - frozenQty),
  );

  return {
    availableQty,
    description: normalizeText(pickValue(record, ['description', 'desc'])),
    frozenQty,
    key: normalizeText(pickValue(record, ['id', 'ID'])) || materialNo || `pallet-${index}`,
    materialNo,
    status: normalizeText(pickValue(record, ['status'])) || 'active',
    stockQty,
  };
}

function getDeliveryNoteLines(note: GeneratedDeliveryNote | null): GeneratedDeliveryNoteLine[] {
  if (!note) {
    return [];
  }
  const nestedLines = note.lines || note.items;
  if (nestedLines?.length) {
    return nestedLines;
  }
  if (note.material_no || note.customer_material_no || note.order_no || note.customer_order_no) {
    return [note as GeneratedDeliveryNoteLine];
  }
  return [];
}

function getDeliveryNoteStatusText(note: GeneratedDeliveryNote) {
  if (note.status_text) {
    return note.status_text;
  }
  if (note.status === 'bound') {
    return '已绑定';
  }
  if (note.status === 'closed') {
    return '已关单';
  }
  return note.status || '待绑定';
}

function isDeliveryNoteLocked(note: GeneratedDeliveryNote | null) {
  return note?.status === 'closed' || note?.status === 'cancelled';
}

function getLineMaterialText(line: GeneratedDeliveryNoteLine) {
  return normalizeText(line.material_no || line.customer_material_no || line.material || line.nw_material) || '--';
}

function getLineDescriptionText(line: GeneratedDeliveryNoteLine) {
  return normalizeText(line.description || line.material_desc || line.customer_material_desc || line.product_description);
}

function getLineQuantity(line: GeneratedDeliveryNoteLine) {
  return toFiniteNumber(line.push_qty ?? line.quantity ?? line.current_quantity, 0);
}

function getLineFieldText(line: GeneratedDeliveryNoteLine, keys: string[]) {
  return normalizeText(pickValue(line as Record<string, unknown>, keys)) || '--';
}

function getSelectedEntries(selectedQuantities: Record<string, number>) {
  return Object.entries(selectedQuantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([materialNo, quantity]) => ({ materialNo, quantity }));
}

function buildSelectedQuantitiesFromPalletItems(items: DeliveryNotePalletDisplayItem[]) {
  return items.reduce<Record<string, number>>((result, item) => {
    result[item.materialNo] = (result[item.materialNo] || 0) + item.quantity;
    return result;
  }, {});
}

function ScanDeliveryPalletBind() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [scanValue, setScanValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [currentNote, setCurrentNote] = useState<GeneratedDeliveryNote | null>(null);
  const [palletRows, setPalletRows] = useState<PalletStockRow[]>([]);
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});
  const [successItems, setSuccessItems] = useState<PalletBindSuccessItem[]>([]);
  const [loadingNote, setLoadingNote] = useState(false);
  const [loadingPallets, setLoadingPallets] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const operator = getStoredUser();
  const selectedEntries = useMemo(() => getSelectedEntries(selectedQuantities), [selectedQuantities]);
  const selectedTotal = useMemo(
    () => selectedEntries.reduce((total, item) => total + item.quantity, 0),
    [selectedEntries],
  );
  const noteNo = getGeneratedDeliveryNoteNo(currentNote);
  const noteLines = getDeliveryNoteLines(currentNote);
  const existingPalletItems = useMemo(() => (
    extractDeliveryNotePalletItems(currentNote as Record<string, unknown> | null)
  ), [currentNote]);
  const existingPalletTotal = useMemo(
    () => existingPalletItems.reduce((total, item) => total + item.quantity, 0),
    [existingPalletItems],
  );
  const filteredPalletRows = useMemo(() => {
    const keyword = searchValue.trim().toUpperCase();
    if (!keyword) {
      return palletRows;
    }
    return palletRows.filter((row) => (
      row.materialNo.toUpperCase().includes(keyword)
      || row.description.toUpperCase().includes(keyword)
    ));
  }, [palletRows, searchValue]);
  const noteLocked = isDeliveryNoteLocked(currentNote);
  const canBind = Boolean(currentNote) && !noteLocked && selectedTotal > 0 && !submitting;

  usePersistentFocus(inputRef, Boolean(operator), currentNote ? `note:${noteNo}` : 'scan-delivery-pallet');

  if (!operator) {
    navigateTo('/login', true);
    return null;
  }

  async function loadPalletStocks() {
    setLoadingPallets(true);
    try {
      const response = await listDeliveryPalletStocks({
        page: 1,
        page_size: 100,
      });
      setPalletRows(extractList<DeliveryPalletStockItem>(response.data).map(mapPalletStock));
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '获取铲板库存失败'));
    } finally {
      setLoadingPallets(false);
    }
  }

  async function locateDeliveryNote(rawCode = scanValue) {
    const code = normalizeScannedCode(rawCode);
    if (!code) {
      notifyError('请先扫描或输入预送货单号');
      return;
    }

    setLoadingNote(true);
    try {
      const response = await listGeneratedDeliveryNotes({
        delivery_note_no: code,
        keyword: code,
        note_no: code,
        page: 1,
        page_size: 20,
      });
      const note = findGeneratedDeliveryNoteByScanCode(response.data, code) as GeneratedDeliveryNote | null;
      if (!note) {
        setCurrentNote(null);
        setSelectedQuantities({});
        setSuccessItems([]);
        notifyError(`没有找到预送货单 ${code}`);
        return;
      }
      const existingItems = extractDeliveryNotePalletItems(note as Record<string, unknown>);
      setCurrentNote(note);
      setSelectedQuantities(buildSelectedQuantitiesFromPalletItems(existingItems));
      setSuccessItems([]);
      setSearchValue('');
      setScanValue(getGeneratedDeliveryNoteNo(note) || code);
      notifySuccess(
        existingItems.length > 0
          ? `已定位,已有 ${existingItems.length} 种铲板`
          : `已定位 ${getGeneratedDeliveryNoteNo(note) || code}`,
      );
      await loadPalletStocks();
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '定位预送货单失败'));
    } finally {
      setLoadingNote(false);
    }
  }

  function resetFlow() {
    setScanValue('');
    setSearchValue('');
    setCurrentNote(null);
    setPalletRows([]);
    setSelectedQuantities({});
    setSuccessItems([]);
    setSubmitting(false);
    setLoadingNote(false);
  }

  function normalizePayloadItems(payloadItems: DeliveryNotePalletItemPayload[]) {
    return payloadItems.map((item) => ({
      materialNo: item.material_no,
      quantity: item.quantity,
    }));
  }

  function updateQuantity(row: PalletStockRow, delta: number) {
    if (!currentNote || noteLocked || successItems.length > 0) {
      return;
    }
    if (row.status !== 'active' || row.availableQty <= 0) {
      return;
    }
    setSelectedQuantities((previous) => {
      const current = previous[row.materialNo] || 0;
      const next = Math.max(0, Math.min(row.availableQty, current + delta));
      const updated = { ...previous };
      if (next <= 0) {
        delete updated[row.materialNo];
      } else {
        updated[row.materialNo] = next;
      }
      return updated;
    });
  }

  async function handleBind() {
    if (!currentNote || !canBind) {
      return;
    }
    const noteId = getGeneratedDeliveryNoteBindId(currentNote);
    if (!noteId) {
      notifyError('预送货单缺少 ID,无法绑定');
      return;
    }
    const payload = buildDeliveryPalletBindPayload(
      selectedEntries.map((item) => ({
        materialNo: item.materialNo,
        quantity: item.quantity,
      })),
    );
    if (payload.pallet_items.length === 0) {
      notifyError('请先选择铲板数量');
      return;
    }
    const confirmText = `确认给 ${noteNo || noteId} 绑定 ${selectedEntries.length} 种铲板,合计 ${selectedTotal} 个?`;
    if (!window.confirm(confirmText)) {
      return;
    }

    setSubmitting(true);
    try {
      await bindGeneratedDeliveryNotePallets(noteId, payload);
      const nextSuccessItems = normalizePayloadItems(payload.pallet_items);
      setSuccessItems(nextSuccessItems);
      setSelectedQuantities({});
      setCurrentNote({
        ...currentNote,
        bound_pallet_quantity: nextSuccessItems.reduce((total, item) => total + item.quantity, 0),
        pallet_items: payload.pallet_items.map((item) => ({
          material_no: item.material_no,
          quantity: item.quantity,
        })),
        status: 'bound',
        status_text: '已绑定',
      });
      notifySuccess('铲板绑定成功');
      await loadPalletStocks();
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '绑定铲板失败'));
    } finally {
      setSubmitting(false);
    }
  }

  function renderStatusChip() {
    if (!currentNote) {
      return <span className="status-chip status-chip--gray">待扫描</span>;
    }
    if (successItems.length > 0 || existingPalletItems.length > 0 || currentNote.status === 'bound') {
      return <span className="status-chip status-chip--green">已绑定</span>;
    }
    if (noteLocked) {
      return <span className="status-chip status-chip--gray">{getDeliveryNoteStatusText(currentNote)}</span>;
    }
    return <span className="status-chip status-chip--orange">待绑定</span>;
  }

  function renderNotePalletChip() {
    if (successItems.length > 0 || existingPalletItems.length > 0 || currentNote?.status === 'bound') {
      return <span className="status-chip status-chip--green">已绑 {existingPalletTotal || selectedTotal || 0}</span>;
    }
    if (currentNote && noteLocked) {
      return <span className="status-chip status-chip--gray">{getDeliveryNoteStatusText(currentNote)}</span>;
    }
    return <span className="status-chip status-chip--orange">待绑定</span>;
  }

  function renderPalletRow(row: PalletStockRow) {
    const selectedQty = selectedQuantities[row.materialNo] || 0;
    const inactive = row.status !== 'active';
    const noStock = row.availableQty <= 0;
    const disabled = inactive || noStock || !currentNote || noteLocked || successItems.length > 0;

    return (
      <article className={disabled ? 'pallet-bind-stock pallet-bind-stock--disabled' : 'pallet-bind-stock'} key={row.key}>
        <div className="pallet-bind-stock__top">
          <div>
            <div className="pallet-bind-stock__code">{row.materialNo || '--'}</div>
            <div className="pallet-bind-stock__desc">{row.description || '无描述'}</div>
          </div>
          {inactive ? (
            <span className="status-chip status-chip--gray">停用</span>
          ) : noStock ? (
            <span className="status-chip status-chip--orange">无可用</span>
          ) : (
            <span className="status-chip status-chip--green">可用</span>
          )}
        </div>
        <div className="pallet-bind-stock__body">
          <div className="pallet-bind-stock__numbers">
            <span>库存 <strong>{row.stockQty}</strong></span>
            <span>可用 <strong>{row.availableQty}</strong></span>
            <span>冻结 <strong>{row.frozenQty}</strong></span>
          </div>
          <div className="pallet-bind-stepper">
            <button
              disabled={selectedQty <= 0 || !currentNote || noteLocked || successItems.length > 0}
              onClick={() => updateQuantity(row, -1)}
              type="button"
            >
              -
            </button>
            <strong>{selectedQty}</strong>
            <button
              disabled={disabled || selectedQty >= row.availableQty}
              onClick={() => updateQuantity(row, 1)}
              type="button"
            >
              +
            </button>
          </div>
        </div>
      </article>
    );
  }

  return (
    <PdaPage
      actionLabel="重置"
      onAction={resetFlow}
      onBack={() => navigateTo('/home')}
      pageClassName="pallet-bind-page"
      subtitle="预送货单号 → 铲板库存 → 保存绑定"
      title="铲板绑定"
      footer={currentNote && successItems.length === 0 ? (
        <div className="pallet-bind-footer">
          <div className="pallet-bind-footer__copy">
            <div>已选 {selectedEntries.length} 种 · {selectedTotal} 个</div>
            <span>
              {selectedEntries.length > 0
                ? selectedEntries.map((item) => `${item.materialNo} x${item.quantity}`).join(' / ')
                : '选择铲板后可提交绑定'}
            </span>
          </div>
          <button className="primary-btn" disabled={!canBind} onClick={() => void handleBind()} type="button">
            {submitting ? '保存中...' : existingPalletItems.length > 0 ? '保存铲板' : '绑定铲板'}
          </button>
        </div>
      ) : null}
    >
      <section className="panel pallet-bind-scan-card">
        <div className="pallet-bind-section-head">
          <div className="panel-title">扫描预送货单号</div>
          {renderStatusChip()}
        </div>
        <div className="pallet-bind-scan-row">
          <input
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            className="scan-input"
            disabled={loadingNote || submitting}
            inputMode="none"
            onChange={(event) => setScanValue(event.target.value)}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void locateDeliveryNote(event.currentTarget.value);
              }
            }}
            placeholder="预送货单号"
            ref={inputRef}
            spellCheck={false}
            value={scanValue}
          />
          <button className="primary-btn" disabled={loadingNote || submitting} onClick={() => void locateDeliveryNote()} type="button">
            {currentNote ? '换单' : loadingNote ? '查询' : '扫码'}
          </button>
        </div>
        <div className="field-help">二维码内容为预送货单号。扫码后只定位已有预送货单，不新增任务。</div>
      </section>

      {successItems.length > 0 ? (
        <section className="pallet-bind-success-card">
          <div className="pallet-bind-success-card__title">绑定成功</div>
          <div className="pallet-bind-success-card__copy">
            已按预送货单 {noteNo || '--'} 冻结铲板库存。
          </div>
          <div className="pallet-bind-success-list">
            {successItems.map((item) => (
              <div className="pallet-bind-success-item" key={item.materialNo}>
                <strong>{item.materialNo}</strong>
                <span>{item.quantity} 个</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {currentNote ? (
        <section className="panel pallet-bind-note-card">
          <div className="pallet-bind-note-card__main">
            <div>
              <div className="pallet-bind-note-card__no">{noteNo || `#${currentNote.id || '--'}`}</div>
              <div className="pallet-bind-note-card__meta">
                客户 {currentNote.customer_code || currentNote.customer_name || '--'} · 采购组 {currentNote.purchase_group || '--'} · 采购员 {currentNote.purchaser || currentNote.buyer || '--'}
              </div>
            </div>
            {renderNotePalletChip()}
          </div>
          <div className="pallet-bind-line-list">
            {noteLines.length > 0 ? noteLines.map((line, index) => (
              <div className="pallet-bind-line" key={String(line.id || line.line_id || index)}>
                <div className="pallet-bind-line__top">
                  <div className="pallet-bind-line__material">
                    <span className="pallet-bind-line__label">物料号</span>
                    <strong>{getLineMaterialText(line)}</strong>
                    <span>{getLineDescriptionText(line) || '--'}</span>
                  </div>
                  <span className="pallet-bind-line__qty">数量 {getLineQuantity(line)}</span>
                </div>
                <div className="pallet-bind-line__primary">
                  <div className="pallet-bind-line__field">
                    <em>订单号</em>
                    <b>{getLineFieldText(line, ['order_no', 'orderNo', 'customer_order_no', 'customerOrderNo'])}</b>
                  </div>
                  <div className="pallet-bind-line__field">
                    <em>行号</em>
                    <b>{getLineFieldText(line, ['line_no', 'lineNo'])}</b>
                  </div>
                  <div className="pallet-bind-line__field">
                    <em>SY号</em>
                    <b>{getLineFieldText(line, ['sy_no', 'syNo'])}</b>
                  </div>
                </div>
                <div className="pallet-bind-line__fields">
                  <div className="pallet-bind-line__field">
                    <em>送货单重</em>
                    <b>{getLineFieldText(line, ['delivery_weight', 'deliveryWeight'])}</b>
                  </div>
                  <div className="pallet-bind-line__field">
                    <em>工厂</em>
                    <b>{getLineFieldText(line, ['factory', 'plant'])}</b>
                  </div>
                  <div className="pallet-bind-line__field">
                    <em>图号</em>
                    <b>{getLineFieldText(line, ['drawing_no', 'drawingNo'])}</b>
                  </div>
                  <div className="pallet-bind-line__field">
                    <em>NW材质</em>
                    <b>{getLineFieldText(line, ['nw_material', 'nwMaterial', 'material'])}</b>
                  </div>
                  <div className="pallet-bind-line__field pallet-bind-line__field--wide">
                    <em>备注</em>
                    <b>{getLineFieldText(line, ['remark'])}</b>
                  </div>
                </div>
              </div>
            )) : (
              <div className="empty-state">当前预送货单没有返回明细。</div>
            )}
          </div>
        </section>
      ) : (
        <section className="panel empty-state">
          先扫描预送货单号。定位后显示订单、物料和铲板库存。
        </section>
      )}

      {existingPalletItems.length > 0 && successItems.length === 0 ? (
        <section className="pallet-bind-existing-card">
          <div className="pallet-bind-existing-card__head">
            <div className="pallet-bind-existing-card__title">已绑定铲板</div>
            <span className="status-chip status-chip--blue">可调整</span>
          </div>
          <div className="pallet-bind-success-list">
            {existingPalletItems.map((item) => (
              <div className="pallet-bind-success-item" key={item.materialNo}>
                <strong>{item.materialNo}</strong>
                <span>{item.quantity} 个</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {currentNote ? (
        <section className="panel pallet-bind-stock-card">
          <div className="pallet-bind-section-head">
            <div className="panel-title">选择铲板</div>
            <span className="status-chip status-chip--blue">
              {loadingPallets ? '加载中' : `${filteredPalletRows.length} 种`}
            </span>
          </div>
          <div className="pallet-bind-search-row">
            <input
              className="field-input"
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="搜索铲板物料号"
              value={searchValue}
            />
            <button
              className="secondary-btn"
              disabled={selectedEntries.length === 0}
              onClick={() => setSearchValue(selectedEntries[0]?.materialNo || '')}
              type="button"
            >
              已选 {selectedEntries.length}
            </button>
          </div>
          <div className="pallet-bind-stock-list">
            {filteredPalletRows.length > 0 ? filteredPalletRows.map(renderPalletRow) : (
              <div className="empty-state">
                {loadingPallets ? '正在获取铲板库存...' : '没有匹配的铲板库存。'}
              </div>
            )}
          </div>
        </section>
      ) : null}
    </PdaPage>
  );
}

export default ScanDeliveryPalletBind;
